import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '../../../../store';
import { DataElementContext } from '../../../../page-components/common/DataElementContext';

import { ticketWinnerFunOpenedListRequest } from '../../../../modules/bets/store/actions/tickets';
import {
  cashoutSubscribe,
  cashoutUnsubscribe,
  cashoutStartProcess,
  cashoutStopProcess,
  cashoutTicket,
  cashoutClearState,
} from '../../../../modules/bets/store/actions/cashout';
import { buildTicketLists } from '../utils/functions';

import './index.scss';
import { useTranslation } from 'react-i18next';

type BetsOpenedProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const BetsOpened = (componentProps: BetsOpenedProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;

  const dispatch = useAppDispatch();
  const opened = useAppSelector<any[]>((state) => state.bets.ticketsWinnerFun.ticketsOpened);
  const inProgress = useAppSelector((state) => state.bets.ticketsWinnerFun.requestInProgress.opened);
  const cashoutState = useAppSelector((state) => state.bets.cashout);
  const liveMatches = useAppSelector((state) => state.bets.live.matches);
  const [subscribeToTickets, setSubscribeToTickets] = React.useState<any[]>([]);
  const [extended, setExtended] = React.useState<any>({});
  const [localCashout, setLocalCashout] = React.useState<any>({
    show: false,
    amount: null,
    hash: null,
  });
  const [agree, setAgree] = React.useState(false);
  const { i18n, t } = useTranslation();

  const toggleAgree = () => {
    setAgree((v) => !v);
  };

  const onExtend = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (e.currentTarget.dataset.hash != null && e.currentTarget.dataset.hash !== '') {
      const hash = e.currentTarget.dataset.hash;
      setExtended((prevState: any) => ({
        ...prevState,
        [hash]: prevState[hash] != null ? !prevState[hash] : true,
      }));
    }
  }, []);

  const onCashout = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget.dataset.hash != null && e.currentTarget.dataset.hash !== '') {
        const hash = e.currentTarget.dataset.hash;
        console.log('onCashout: ', hash);

        const ticket = opened.find((t) => t.idHash === hash);
        if (ticket != null) {
          const tickets: any = cashoutState?.tickets ?? null;
          const cashout: any = tickets?.[hash];

          setLocalCashout((prevState: any) => ({
            ...prevState,
            show: true,
            amount: cashout?.cashoutAmountAfterTax,
            hash: hash,
          }));
        }
      }
    },
    [opened, cashoutState],
  );

  const hideCashout = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    dispatch(cashoutClearState());
    setLocalCashout({
      show: false,
      amount: null,
      hash: null,
    });
  }, []);

  const hideCashoutSuccess = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    dispatch(cashoutClearState());
    dispatch(ticketWinnerFunOpenedListRequest({ ticketsFetchMeta: true }));
    setLocalCashout({
      show: false,
      amount: null,
      hash: null,
    });
  }, []);

  const doCashout = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget.dataset.hash != null && e.currentTarget.dataset.hash !== '' && !cashoutState?.inProgress) {
        const hash = e.currentTarget.dataset.hash;
        console.log('onCashout: ', hash);

        const ticket = opened.find((t) => t.idHash === hash);

        if (ticket != null) {
          const tickets: any = cashoutState?.tickets ?? null;
          const cashout: any = tickets?.[hash];

          if (cashout?.available && cashout?.cashoutAmountAfterTax) {
            dispatch(
              cashoutTicket({
                provider: ticket.provider,
                ticket: hash,
                amount: cashout?.cashoutAmountAfterTax,
                isWinnerFun: false,
                ticketType: ticket.format,
                agreeChanges: agree,
              }),
            );
          }
        }
      }
    },
    [opened, cashoutState, agree],
  );

  React.useEffect(() => {
    dispatch(ticketWinnerFunOpenedListRequest({ ticketsFetchMeta: true }));
  }, []); //eslint-disable-line

  React.useEffect(() => {
    const toSubscribe: any[] = [];

    opened.forEach((t) => {
      if (t && t.format === 'live') {
        toSubscribe.push({ ticketHash: t.idHash, provider: t.provider });
      }
    });

    let isChanged = false;
    if (toSubscribe.length !== subscribeToTickets.length) {
      isChanged = true;
    } else {
      toSubscribe.forEach((t) => {
        if (!subscribeToTickets.find((s) => s.ticketHash === t.ticketHash)) {
          isChanged = true;
        }
      });
    }

    if (isChanged) {
      setSubscribeToTickets(toSubscribe);
    }
  }, [opened]);

  React.useEffect(() => {
    if (subscribeToTickets.length) {
      dispatch(cashoutStartProcess());
      dispatch(cashoutSubscribe({ tickets: subscribeToTickets, isWinnerFun: true, type: 'live' }));
    }

    return () => {
      if (subscribeToTickets.length) {
        dispatch(cashoutUnsubscribe({ tickets: subscribeToTickets, isWinnerFun: true, type: 'live' }));
        dispatch(cashoutStopProcess());
      }
    };
  }, [subscribeToTickets]);

  const contextValue = React.useMemo(() => {
    let cashoutData: any = null;
    const cashoutTickets: any = cashoutState?.tickets ?? null;
    if (localCashout.show && localCashout.hash != null && cashoutTickets?.[localCashout.hash] != null) {
      cashoutData = cashoutTickets[localCashout.hash];
    }

    const contextValue = {
      tickets: buildTicketLists(opened, extended, i18n.language, t, true, cashoutState, liveMatches),
      inProgress,
      onExtend,
      onCashout,
      showCashoutModal: localCashout.show,
      hideCashoutModal: hideCashout,
      hideCashoutSuccessModal: hideCashoutSuccess,
      cashoutHash: localCashout.hash,
      cashoutAmount: localCashout.amount,
      cashoutCurrency: 'Lei',
      cashoutInProgress: cashoutState?.inProgress ?? false,
      cashoutData: cashoutData,
      cashoutResult: cashoutState?.cashoutResult ?? null,
      doCashout,
      agree,
      toggleAgree,
    };

    return contextValue;
  }, [
    opened,
    cashoutState,
    localCashout,
    inProgress,
    extended,
    onExtend,
    onCashout,
    hideCashout,
    hideCashoutSuccess,
    doCashout,
    liveMatches,
    agree,
    toggleAgree,
    i18n.language,
    t,
  ]);

  //console.log('BetsOpened[contextValue]: ', contextValue, cashoutState?.inProgress);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default BetsOpened;
